import './ToggleGroup.scss'
import { useRef, type ComponentProps, type KeyboardEvent, type ReactNode } from 'react'
import { clx } from '../clx'

export interface ToggleGroupOption<T extends string = string> {
  value: T
  label: ReactNode
  /** Decorative icon before the label. */
  icon?: ReactNode
  disabled?: boolean
}

/** A radiogroup needs a name: one of aria-label or aria-labelledby. */
type Labelling =
  | { 'aria-label': string; 'aria-labelledby'?: never }
  | { 'aria-labelledby': string; 'aria-label'?: never }

export type ToggleGroupProps<T extends string = string> = Omit<
  ComponentProps<'div'>,
  'onChange' | 'role' | 'children' | 'defaultValue' | 'aria-label' | 'aria-labelledby'
> &
  Labelling & {
    options: ToggleGroupOption<T>[]
    /** Fully controlled: the group never changes its own value. */
    value: T
    /** Called with a new value only; the caller may confirm before committing it. */
    onChange: (value: T) => void
    /** Full width with equal segments. */
    isBlock?: boolean
    isDisabled?: boolean
    size?: 'sm' | 'md'
  }

const NEXT_KEYS: Record<string, 1 | -1 | 'first' | 'last'> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
  Home: 'first',
  End: 'last'
}

/**
 * Exclusive single choice as a segmented control (WAI-ARIA radio group):
 * roving tabindex, and arrow keys/Home/End move focus and select.
 */
export function ToggleGroup<T extends string = string>({
  options,
  value,
  onChange,
  isBlock = false,
  isDisabled = false,
  size = 'md',
  className,
  onKeyDown,
  ...rest
}: ToggleGroupProps<T>) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([])
  const enabled = options.flatMap((o, i) => (o.disabled ? [] : [i]))
  const checkedIndex = options.findIndex((o) => o.value === value)
  const tabbable =
    checkedIndex >= 0 && !options[checkedIndex]?.disabled ? checkedIndex : (enabled[0] ?? -1)

  const select = (index: number) => {
    const option = options[index]
    if (!option || option.disabled || isDisabled) return
    if (option.value !== value) onChange(option.value)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event)
    const move = NEXT_KEYS[event.key]
    if (move === undefined || isDisabled || enabled.length === 0) return
    event.preventDefault()
    const from = buttons.current.findIndex((b) => b === document.activeElement)
    const pos = enabled.indexOf(from >= 0 ? from : tabbable)
    let target: number | undefined
    if (move === 'first') target = enabled[0]
    else if (move === 'last') target = enabled.at(-1)
    else target = enabled[(pos + move + enabled.length) % enabled.length]
    if (target === undefined) return
    buttons.current[target]?.focus()
    select(target)
  }

  return (
    <div
      {...rest}
      role="radiogroup"
      aria-disabled={isDisabled || undefined}
      className={clx(
        'toggle-group',
        isBlock && 'toggle-group--is-block',
        size !== 'md' && `toggle-group--is-${size}`,
        className
      )}
      onKeyDown={handleKeyDown}
    >
      {options.map((option, index) => (
        <button
          key={option.value}
          ref={(node) => {
            buttons.current[index] = node
          }}
          type="button"
          role="radio"
          className="toggle-group__option"
          aria-checked={option.value === value}
          tabIndex={index === tabbable ? 0 : -1}
          disabled={isDisabled || option.disabled}
          onClick={() => {
            select(index)
          }}
        >
          {option.icon && (
            <span className="toggle-group__icon" aria-hidden="true">
              {option.icon}
            </span>
          )}
          {option.label}
        </button>
      ))}
    </div>
  )
}
